import { isServerStartGuideStatus } from "./startGuideStatusSource";

const completionOf = (stepId, steps) =>
  Boolean(steps.find((step) => step.id === stepId)?.complete);

/**
 * Merges guide content with either the client record or a server payload.
 * A signal may force a step open or closed when the browser already knows
 * the underlying data. `null` means "no signal, use the stored flag".
 */
export function resolveStartGuideSteps({
  steps = [],
  clientSteps = {},
  serverStatus = null,
  signals = {},
}) {
  const useServer = isServerStartGuideStatus() && serverStatus?.steps;

  const withCompletion = steps.map((step) => {
    if (step.category === "learn") {
      return { ...step, complete: false, locked: false, lockReason: "" };
    }

    const serverStep = useServer ? serverStatus.steps[step.id] : null;
    let complete = false;

    if (serverStep && typeof serverStep.complete === "boolean") {
      complete = serverStep.complete;
    } else {
      const signal = signals[step.id];
      const signalValue = typeof signal === "function" ? signal() : signal;
      if (signalValue === true) complete = true;
      else if (signalValue === false) complete = false;
      else complete = Boolean(clientSteps[step.id]?.complete);
    }

    return { ...step, complete, locked: false, lockReason: step.lockReason || "" };
  });

  return withCompletion.map((step) => {
    if (step.category === "learn") return step;

    const serverStep = useServer ? serverStatus.steps[step.id] : null;
    if (serverStep && typeof serverStep.locked === "boolean") {
      return {
        ...step,
        locked: serverStep.locked,
        lockReason: serverStep.lockReason || step.lockReason || "",
      };
    }

    const missing = (step.requires || []).filter((id) => !completionOf(id, withCompletion));
    if (missing.length === 0) return { ...step, locked: false };

    return {
      ...step,
      locked: true,
      lockReason: step.lockReason || "Complete the previous required step first.",
    };
  });
}

export function countRequiredProgress(steps = []) {
  const required = steps.filter((step) => (step.category || "required") === "required");
  const source = required.length > 0 ? required : steps.filter((step) => step.category !== "learn");
  const total = source.length;
  const done = source.filter((step) => step.complete).length;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    complete: total > 0 && done === total,
  };
}
